import type {
  HealthcareService,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { formatTime } from '@seen/core/dates'
import type * as appointments from '@seen/core/models/appointments/index'
import type { AppointmentSlotSelected } from '@seen/request-types/suggestAppointmentSlots'
import { Button, cn } from '@seen/ui'
import {
  IconInfoCircle,
  IconRefresh,
  IconSparkles,
  IconThumbDown,
  IconThumbUp,
} from '@tabler/icons-react'
import { format, isSameMinute } from 'date-fns'
import { type JSX, useEffect, useRef, useState } from 'react'
import {
  type AiAppointmentSlot,
  useAiRecommendedAppointmentSlots,
} from '#src/components/appointment/useAiRecommendedAppointmentSlots.ts'
import { FhirQueryLoader } from '#src/components/core/FhirQueryLoader.tsx'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#src/components/core/index.ts'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import { useScrollIntoViewWhenSelected } from '#src/hooks/useScrollIntoViewWhenSelected.ts'

// Fixed height of the slots scroll region. Held constant across loading and
// loaded states so the skeleton and the real list occupy the same box and the
// surrounding form doesn't shift when suggestions arrive.
// Caps the scrollable list rather than reserving that height unconditionally,
// so a short result doesn't leave dead space above the rest of the form.
// Matches the engine list so switching paths doesn't resize the form — see
// its note on why the cap has to be on the viewport as well as the root.
const SLOTS_VIEWPORT_CLASS = 'h-auto! max-h-48'
// The loading skeleton keeps the fixed height: reserving the space it is
// about to fill is what stops the form jumping when slots land.
const SLOTS_SKELETON_CLASS = 'h-48'

interface AiRecommendedAppointmentSlotsProps {
  patient: Reference<Patient> | undefined
  healthcareService: Reference<HealthcareService> | undefined
  practitioner: Reference<Practitioner> | undefined
  onAppointmentSlotSelected: (
    slot: appointments.AvailableAppointmentSlot
  ) => void
  // Reports which suggestion was picked (with its Langfuse trace) so the parent
  // can record it as chosen only once the appointment is actually booked.
  onSlotSelectedTelemetry?: (selection: AppointmentSlotSelected) => void
  // Reports the full set of currently suggested slots so the parent can draw
  // them on the calendar grid; called with [] while none are loaded.
  onAvailableSlotsChange?: (slots: { start: string; end: string }[]) => void
  // Reports the current generation's trace + suggested slot starts so the
  // parent can, on a manual (non-suggested) booking, record a disagreement
  // score against the trace. Null while no generation has landed.
  onSuggestionContextChange?: (
    context: { traceId: string; suggestedStarts: string[] } | null
  ) => void
  selectedStart: string | undefined
  selectedEnd: string | undefined
  // The calendar's visible horizon start; suggestions are generated from this
  // day forward. Undefined suggests from today.
  searchFrom?: string
  // The visit being moved, dropped from the conflict calendar.
  excludeAppointmentId?: string
}

// Header controls for the AI suggestion set: an "AI-generated" marker, thumbs
// up/down, and a regenerate affordance. Thumbs up records immediately; thumbs
// down records and opens the guidance popover (steering the retry). The
// guidance box lives in a Popover anchored to the regenerate button so it
// floats over the layout instead of pushing the form content down.
function AiSuggestionFeedbackControls({
  thumbValue,
  onThumb,
  onRegenerate,
  isRegenerating,
}: {
  thumbValue: 0 | 1 | undefined
  onThumb: (value: 0 | 1) => void
  onRegenerate: (feedbackText?: string) => void
  isRegenerating: boolean
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')

  const submit = (): void => {
    onRegenerate(text.trim() || undefined)
    setText('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center text-muted-foreground">
              <IconSparkles size={14} />
            </span>
          </TooltipTrigger>
          <TooltipContent>AI-generated suggestions</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="2xs"
              aria-label="Good suggestions"
              aria-pressed={thumbValue === 1}
              onClick={(evt) => {
                evt.preventDefault()
                onThumb(1)
              }}
            >
              <IconThumbUp
                size={14}
                className={cn(
                  thumbValue === 1 && 'fill-current text-green-600'
                )}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>These look good</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="2xs"
              aria-label="Poor suggestions"
              aria-pressed={thumbValue === 0}
              onClick={(evt) => {
                evt.preventDefault()
                onThumb(0)
                setOpen(true)
              }}
            >
              <IconThumbDown
                size={14}
                className={cn(thumbValue === 0 && 'fill-current text-red-600')}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>These aren’t right</TooltipContent>
        </Tooltip>
        {/* PopoverTrigger doubles as the tooltip target; a plain title attr
            avoids nesting two Radix triggers on the same button. */}
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="2xs"
            aria-label="Regenerate suggestions"
            title="Regenerate, optionally with guidance"
            loading={isRegenerating}
          >
            <IconRefresh size={14} />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent align="end" className="flex w-72 flex-col gap-2 p-3">
        <p className="text-xs font-medium">Regenerate suggestions</p>
        <Textarea
          aria-label="Regenerate guidance"
          placeholder="Optional: what should change? (e.g. prefer mornings, sooner)"
          value={text}
          onChange={setText}
          className="min-h-[56px] text-xs"
        />
        <div className="flex justify-end gap-1.5">
          <Button
            variant="ghost"
            size="xs"
            onClick={(evt) => {
              evt.preventDefault()
              setText('')
              setOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            loading={isRegenerating}
            onClick={(evt) => {
              evt.preventDefault()
              submit()
            }}
          >
            Regenerate
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Fixed "Next Available" header: the label plus the optional AI-feedback
// cluster (thumbs + regenerate). Rendered in both the loading and loaded states
// so it stays put while slots stream in.
function AiRecommendedAppointmentSlotsHeader({
  thumbValue,
  onThumb,
  onRegenerate,
  isRegenerating = false,
}: {
  thumbValue?: 0 | 1
  onThumb?: (value: 0 | 1) => void
  onRegenerate?: (feedbackText?: string) => void
  isRegenerating?: boolean
}): JSX.Element {
  const showFeedback = !!onThumb && !!onRegenerate

  return (
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs font-medium">Next Available</p>
      {showFeedback && (
        <AiSuggestionFeedbackControls
          thumbValue={thumbValue}
          onThumb={onThumb}
          onRegenerate={onRegenerate}
          isRegenerating={isRegenerating}
        />
      )}
    </div>
  )
}

// Loading placeholder for the slots list: the fixed header plus skeleton rows
// filling the same viewport height as the loaded list, so content doesn't shift
// when real slots arrive.
export function AiRecommendedAppointmentSlotsSkeleton({
  isRegenerating = false,
}: {
  isRegenerating?: boolean
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <AiRecommendedAppointmentSlotsHeader isRegenerating={isRegenerating} />
      <div className={cn('flex flex-col gap-1.5 pr-3', SLOTS_SKELETON_CLASS)}>
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton
            // Static placeholder list; index keys are stable and never reorder.
            key={index}
            className="h-9 w-full"
          />
        ))}
      </div>
    </div>
  )
}

// Error state: keep the "Next Available" header in place so the whole section
// doesn't vanish on a failed generation, and offer a Retry.
export function AiRecommendedAppointmentSlotsError({
  onRetry,
  isRetrying = false,
}: {
  onRetry: () => void
  isRetrying?: boolean
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <AiRecommendedAppointmentSlotsHeader />
      <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-gray-200 p-4 text-center">
        <p className="text-sm text-muted-foreground">
          Couldn’t generate suggestions.
        </p>
        <Button
          variant="outline"
          size="xs"
          loading={isRetrying}
          onClick={(evt) => {
            evt.preventDefault()
            onRetry()
          }}
        >
          <IconRefresh size={14} className="mr-1" />
          Retry
        </Button>
      </div>
    </div>
  )
}

// Empty state: a generation succeeded but yielded no bookable slots (every
// candidate overlapped a real conflict, or the model returned none). Keep the
// "Next Available" header and its feedback cluster so the section doesn't just
// vanish, and tell the scheduler why plus how to steer a retry.
export function AiRecommendedAppointmentSlotsEmpty({
  thumbValue,
  onThumb,
  onRegenerate,
  isRegenerating = false,
}: {
  thumbValue?: 0 | 1
  onThumb?: (value: 0 | 1) => void
  onRegenerate?: (feedbackText?: string) => void
  isRegenerating?: boolean
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <AiRecommendedAppointmentSlotsHeader
        thumbValue={thumbValue}
        onThumb={onThumb}
        onRegenerate={onRegenerate}
        isRegenerating={isRegenerating}
      />
      <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-gray-200 p-4 text-center">
        <p className="text-sm text-muted-foreground">
          No open slots found in the next few weeks.
        </p>
        <p className="text-xs text-muted-foreground">
          Try regenerating with different guidance, or pick a time manually.
        </p>
      </div>
    </div>
  )
}

// A reason, when present, is surfaced behind an info icon at the trailing
// edge rather than as a tooltip over the whole row.
function AiSlotRow({
  slot,
  isSelected,
  onSelect,
}: {
  slot: AiAppointmentSlot
  isSelected: boolean
  onSelect: () => void
}): JSX.Element {
  const buttonRef = useScrollIntoViewWhenSelected<HTMLButtonElement>(isSelected)

  return (
    <div className="flex items-center gap-1">
      <Button
        ref={buttonRef}
        variant="ghost"
        onClick={(evt) => {
          evt.preventDefault()
          evt.stopPropagation()
          onSelect()
        }}
        // Google-style "available slot" rows: a solid green left accent bar
        // with a soft green wash. The selected row reads as filled/bordered
        // (opaque tint + full green border) the way the chosen block does on
        // the calendar grid.
        className={cn(
          'h-auto w-full flex-1 justify-start rounded-sm border border-transparent border-l-4 border-l-green-600 bg-green-50/60 py-1.5 text-left font-normal text-gray-900 text-sm hover:bg-green-50',
          isSelected &&
            'border-green-600 bg-green-100 font-medium hover:bg-green-100'
        )}
      >
        {format(slot.period.start, 'EEE, MMM d')} ·{' '}
        {formatTime(slot.period.start)} – {formatTime(slot.period.end)}
      </Button>
      {slot.reason && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              // Non-interactive marker; the tooltip fires on hover.
              // A span (not a button) keeps it out of the tab order
              // and avoids nesting a second clickable in the row.
              // role="img" makes the aria-label a valid accessible
              // name for the icon.
              role="img"
              className="flex shrink-0 items-center text-muted-foreground"
              aria-label="Why this slot"
            >
              <IconInfoCircle size={14} />
            </span>
          </TooltipTrigger>
          <TooltipContent>{slot.reason}</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

// Presentational "Next Available" list: slots ranked best-first, each a
// green-accented row with a "why" tooltip (preference vs rule). Stateless so
// it can be storied without network/query wiring. The optional feedback props
// render the AI-feedback header cluster (thumbs + regenerate); omit them and
// the header is just the label. Renders the empty state (not null) when there
// are no slots, so a fully-filtered generation still surfaces something.
export function AiRecommendedAppointmentSlotsList({
  slots,
  selectedStart,
  selectedEnd,
  onSelect,
  thumbValue,
  onThumb,
  onRegenerate,
  isRegenerating = false,
}: {
  slots: AiAppointmentSlot[]
  selectedStart: string | undefined
  selectedEnd: string | undefined
  onSelect: (slot: AiAppointmentSlot, index: number) => void
  thumbValue?: 0 | 1
  onThumb?: (value: 0 | 1) => void
  onRegenerate?: (feedbackText?: string) => void
  isRegenerating?: boolean
}): JSX.Element {
  if (!slots.length) {
    return (
      <AiRecommendedAppointmentSlotsEmpty
        thumbValue={thumbValue}
        onThumb={onThumb}
        onRegenerate={onRegenerate}
        isRegenerating={isRegenerating}
      />
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <AiRecommendedAppointmentSlotsHeader
        thumbValue={thumbValue}
        onThumb={onThumb}
        onRegenerate={onRegenerate}
        isRegenerating={isRegenerating}
      />
      {/* Radix ScrollArea's viewport only scrolls when the root has a bounded
          height, so cap it directly here (not via a wrapper). */}
      <ScrollArea
        scrollRestorationId={null}
        className={SLOTS_VIEWPORT_CLASS}
        contentClassName={SLOTS_VIEWPORT_CLASS}
      >
        <div className="flex flex-col gap-1.5 pr-3">
          {slots.map((slot, index) => (
            <AiSlotRow
              key={slot.id}
              slot={slot}
              isSelected={Boolean(
                selectedStart &&
                isSameMinute(slot.period.start, selectedStart) &&
                selectedEnd &&
                isSameMinute(slot.period.end, selectedEnd)
              )}
              onSelect={() => onSelect(slot, index)}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

// AI-generated "Next Available" list. Unlike the deterministic finder, this
// auto-fetches whenever patient + service + practitioner are set.
export function AiRecommendedAppointmentSlots(
  props: AiRecommendedAppointmentSlotsProps
): JSX.Element {
  const { query, regenerate, isRegenerating, recordFeedback } =
    useAiRecommendedAppointmentSlots({
      patient: props.patient,
      healthcareService: props.healthcareService,
      practitioner: props.practitioner,
      searchFrom: props.searchFrom,
      excludeAppointmentId: props.excludeAppointmentId,
    })

  // Local record of the current thumb so the button reflects the last rating.
  // Reset when a regenerate is kicked off, since a new set is being judged.
  const [thumbValue, setThumbValue] = useState<0 | 1 | undefined>(undefined)

  // Mirror the current suggestions up to the host (for the calendar grid),
  // clearing on unmount so the grid doesn't keep stale blocks after the form
  // closes.
  const slots = query.data?.slots
  const traceId = query.data?.traceId
  const onAvailableSlotsChange = props.onAvailableSlotsChange
  const onChangeRef = useRef(onAvailableSlotsChange)
  onChangeRef.current = onAvailableSlotsChange
  useEffect(() => {
    onChangeRef.current?.(
      slots?.map((slot) => ({
        start: slot.period.start,
        end: slot.period.end,
      })) ?? []
    )
  }, [slots])
  useEffect(() => () => onChangeRef.current?.([]), [])

  // Mirror the current generation's trace + suggested starts up to the host so
  // a manual (non-suggested) booking can be scored as a disagreement against
  // this trace. Cleared on unmount alongside the grid slots above.
  const onContextChange = props.onSuggestionContextChange
  const onContextChangeRef = useRef(onContextChange)
  onContextChangeRef.current = onContextChange
  useEffect(() => {
    // During a regenerate the query data is briefly undefined; don't publish
    // null then, or a booking landing in that gap would clear the last
    // generation and record neither telemetry outcome. Keep the prior context
    // until the new trace arrives (or the component unmounts).
    if (!traceId && isRegenerating) {
      return
    }
    onContextChangeRef.current?.(
      traceId
        ? {
            traceId,
            suggestedStarts: slots?.map((slot) => slot.period.start) ?? [],
          }
        : null
    )
  }, [traceId, slots, isRegenerating])
  useEffect(() => () => onContextChangeRef.current?.(null), [])

  return (
    <FhirQueryLoader
      query={query}
      loader={<AiRecommendedAppointmentSlotsSkeleton />}
      error={() => (
        <AiRecommendedAppointmentSlotsError
          isRetrying={isRegenerating}
          onRetry={() => {
            setThumbValue(undefined)
            regenerate()
          }}
        />
      )}
    >
      {(data) => {
        // No generation yet (refs not ready) — render nothing. A completed
        // generation with zero slots still renders the list, which shows its
        // empty state rather than vanishing.
        if (!data) {
          return null
        }

        return (
          <AiRecommendedAppointmentSlotsList
            slots={data.slots}
            selectedStart={props.selectedStart}
            selectedEnd={props.selectedEnd}
            thumbValue={thumbValue}
            isRegenerating={isRegenerating}
            onThumb={(value) => {
              setThumbValue(value)
              recordFeedback(value)
            }}
            onRegenerate={(feedbackText) => {
              setThumbValue(undefined)
              regenerate(feedbackText)
            }}
            onSelect={(slot, index) => {
              props.onSlotSelectedTelemetry?.({
                traceId: data.traceId,
                selectedRank: slot.rank,
                selectedIndex: index,
                totalSuggested: data.slots.length,
              })
              props.onAppointmentSlotSelected({
                id: slot.id,
                period: slot.period,
                practitioners: slot.practitioners,
              })
            }}
          />
        )
      }}
    </FhirQueryLoader>
  )
}
